import { useCallback, useEffect, useRef, useState } from "react";
import ContainerCustom from "../../../Components/ContainerCustom";
import Table from "../../../Components/Table/Table";
import TableButton from "../../../Components/Buttons/TableButton";
import { API_BASE_URL } from "../../../config/api";
import { observeInfiniteScroll } from "../../../helper/observeInfiniteScroll";

const USERS_ENDPOINT = `${API_BASE_URL}/api/v0/auth/admin/users/all/`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function buildUsersUrl({ page, searchTerm }) {
  const params = new URLSearchParams();

  params.set("page", String(page));

  if (searchTerm) {
    params.set("search", searchTerm);
  }

  return `${USERS_ENDPOINT}?${params.toString()}`;
}

function mapUserRow(user) {
  return {
    id: user.id,
    uuid: user.code ?? "-",
    email: user.email ?? "-",
    name: user.fn && user.ln ? `${user.fn} ${user.ln}` : "-",
    nationalId: user.national_id || "-",
    phoneNumber: user.phone_number || "-",
    detail: "",
  };
}

function mergeRows(previousRows, nextRows) {
  const seen = new Set(previousRows.map((row) => row.id));
  const uniqueRows = nextRows.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });

  return [...previousRows, ...uniqueRows];
}

export default function CustomerList() {
  const [searchState, setSearchState] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);
  const loadMoreRef = useRef(null);

  const fetchUsersData = useCallback(
    async ({ page = 1, replace = false, searchTerm }) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setError("");

      if (replace) {
        setIsLoading(true);
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const data = await authorizedGet(
          buildUsersUrl({
            page,
            searchTerm,
          }),
        );

        if (!data || requestId !== requestIdRef.current) {
          return;
        }

        const mappedRows = Array.isArray(data.results)
          ? data.results.map(mapUserRow)
          : [];

        setTableData((previousRows) =>
          replace ? mappedRows : mergeRows(previousRows, mappedRows),
        );
        setPagination({
          page: data.page ?? page,
          count: data.count ?? mappedRows.length,
          pageSize: data.page_size ?? mappedRows.length,
          totalPages: data.total_pages ?? page,
        });
      } catch (fetchError) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        console.error("Error fetching customer data:", fetchError);
        setError("خطا در دریافت لیست مشتریان");

        if (replace) {
          setTableData([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    fetchUsersData({
      page: 1,
      replace: true,
      searchTerm: appliedSearch,
    });
  }, [appliedSearch, fetchUsersData]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) {
      return;
    }

    return observeInfiniteScroll(target, () => {
        if (
          isLoading ||
          isLoadingMore ||
          pagination.page >= pagination.totalPages
        ) {
          return;
        }

        fetchUsersData({
          page: pagination.page + 1,
          replace: false,
          searchTerm: appliedSearch,
        });
      });
  }, [
    appliedSearch,
    fetchUsersData,
    isLoading,
    isLoadingMore,
    pagination.page,
    pagination.totalPages,
  ]);

  const handleSearchClick = () => {
    const nextSearch = searchState.trim();

    if (nextSearch === appliedSearch) {
      fetchUsersData({
        page: 1,
        replace: true,
        searchTerm: nextSearch,
      });
      return;
    }

    setAppliedSearch(nextSearch);
  };

  return (
    <ContainerCustom
      header="لیست مشتریان"
      headSVG="archive"
      filter={false}
      sort={false}
      search={true}
      searchValue={searchState}
      setSearchState={setSearchState}
      searchClick={handleSearchClick}
    >
      <div className="flex flex-col">
        <Table
          columns={[
            { key: "uuid", label: "کد مشتری" },
            { key: "email", label: "ایمیل" },
            { key: "name", label: "نام" },
            { key: "nationalId", label: "کد ملی" },
            { key: "phoneNumber", label: "شماره تلفن همراه" },
            {
              key: "detail",
              label: "جزئیات",
              render: (value, row) => (
                <TableButton
                  text="مشاهده"
                  href=""
                  onClick={() => {
                    window.location.href = `/customers/${row.id}/customer-info`;
                  }}
                  className="!py-1 !px-2 !rounded-md bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
                />
              ),
            },
          ]}
          data={tableData}
          loading={isLoading}
          loadingMore={isLoadingMore}
          error={error}
          onRetry={() => fetchUsersData(1, false)}
          showEmptyState
          emptyMessage="نتیجه‌ای مطابق جستجو یافت نشد"
          bottomRef={loadMoreRef}
        />
      </div>
    </ContainerCustom>
  );
}
